import { useEffect, useState } from 'react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ApiError } from '@/lib/api';

export type AcaoVersao = 'enviar' | 'aprovar' | 'descartar' | 'custos-atuais';

const TEXTO: Record<AcaoVersao, { titulo: (n: string) => string; descricao: string; botao: string; destrutiva?: boolean }> = {
  enviar: {
    titulo: (n) => `Enviar a versão ${n} ao cliente?`,
    descricao:
      'A versão fica CONGELADA: itens, preços, validade e dados da empresa não mudam mais. O PDF é gerado e guardado com hash (sha256). Para alterar depois, crie a próxima versão.',
    botao: 'Enviar ao cliente',
  },
  aprovar: {
    titulo: (n) => `Aprovar a versão ${n}?`,
    descricao:
      'Só esta versão passa a valer para o orçamento. As demais ficam como histórico. Um rascunho aberto, se houver, é descartado.',
    botao: 'Aprovar esta versão',
  },
  descartar: {
    titulo: (n) => `Descartar o rascunho ${n}?`,
    descricao: 'O rascunho deixa de ser editável e fica como descartado no histórico. A versão de referência continua a última enviada.',
    botao: 'Descartar rascunho',
    destrutiva: true,
  },
  'custos-atuais': {
    titulo: (n) => `Gerar nova versão com custos atuais a partir de ${n}?`,
    descricao:
      'Cria a próxima versão como RASCUNHO: os preços negociados, a tabela e a condição são os da versão enviada, e o custo e o lucro são recalculados com os custos de hoje. A versão enviada não muda. Nada é enviado ao cliente.',
    botao: 'Gerar nova versão com custos atuais',
  },
};

export function mensagemErroVersao(e: unknown): string {
  return e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API';
}

export interface DialogoAcaoVersaoProps {
  /** Acao pendente; nulo = fechado. */
  acao: AcaoVersao | null;
  /** Numero exibido da versao (`33392-v2`). */
  numeroExibido: string;
  /** Chama a API. Se rejeitar, o dialogo continua aberto e mostra a mensagem e o `code`. */
  onConfirmar: () => Promise<void>;
  onCancelar: () => void;
}

/** Confirmacao das acoes de versao (enviar congela; aprovar escolhe a versao; descartar apaga o rascunho para o cliente). */
export function DialogoAcaoVersao({ acao, numeroExibido, onConfirmar, onCancelar }: DialogoAcaoVersaoProps) {
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  useEffect(() => {
    setErro(null);
    setOcupado(false);
  }, [acao]);

  const t = acao ? TEXTO[acao] : null;

  async function confirmar() {
    setErro(null);
    setOcupado(true);
    try {
      await onConfirmar();
    } catch (e) {
      setErro(mensagemErroVersao(e));
      setOcupado(false);
    }
  }

  return (
    <AlertDialog open={acao !== null} onOpenChange={(o) => !o && !ocupado && onCancelar()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t ? t.titulo(numeroExibido) : ''}</AlertDialogTitle>
          <AlertDialogDescription>{t?.descricao ?? ''}</AlertDialogDescription>
        </AlertDialogHeader>
        {erro && (
          <p role="alert" data-testid="erro-acao-versao" className="rounded-xl border border-destructive/30 bg-danger-bg px-3 py-2 text-small text-danger-ink">
            {erro}
          </p>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={ocupado}>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            disabled={ocupado}
            onClick={(e) => {
              e.preventDefault();
              void confirmar();
            }}
            className={t?.destrutiva ? 'bg-destructive text-white hover:bg-destructive/90' : undefined}
          >
            {t ? t.botao : ''}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
